import { ChangeEvent, KeyboardEvent, useState } from 'react'
import { useIntl } from 'react-intl'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import ChatIcon from '@mui/icons-material/Chat'
import { AiAgentMessageList } from '../../features/AiAgentChat/AiAgentMessageList'
import { useAiAgentConversation } from '../../features/AiAgentChat/hooks/useAiAgentConversation'
import { usePageContext } from '../../features/AiAgentChat/hooks/usePageContext'
import {
  ChatFab,
  ChatPanel,
  PanelHeader,
  HeaderTitle,
  HeaderActions,
  InputArea,
  ChatInput,
  SendButton,
  HeaderIconButton,
  InlineError,
} from './styles'

export const AiAgentChat = () => {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const { messages, isStreaming, inlineError, sendMessage, resetConversation } =
    useAiAgentConversation({
      greetingText: intl.formatMessage({ id: 'aiAgentChat.greeting' }),
      busyErrorText: intl.formatMessage({ id: 'aiAgentChat.error.busy' }),
      generalErrorText: intl.formatMessage({ id: 'aiAgentChat.error.general' }),
      streamErrorText: intl.formatMessage({
        id: 'aiAgentChat.error.streaming',
      }),
    })
  const pageContext = usePageContext()

  const handleSendMessage = () => {
    if (isStreaming || inputValue.trim() === '') {
      return
    }
    const messageText = inputValue
    setInputValue('')
    sendMessage(messageText, pageContext)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const handleRefresh = () => {
    resetConversation()
    setInputValue('')
  }

  return (
    <>
      {open && (
        <ChatPanel>
          <PanelHeader>
            <HeaderTitle>
              {intl.formatMessage({ id: 'aiAgentChat.title' })}
            </HeaderTitle>
            <HeaderActions>
              <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.reset' })}>
                <span>
                  <HeaderIconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={isStreaming}
                  >
                    <RefreshIcon fontSize="small" />
                  </HeaderIconButton>
                </span>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.close' })}>
                <HeaderIconButton size="small" onClick={() => setOpen(false)}>
                  <CloseIcon fontSize="small" />
                </HeaderIconButton>
              </Tooltip>
            </HeaderActions>
          </PanelHeader>
          <AiAgentMessageList messages={messages} isStreaming={isStreaming} />
          {inlineError && <InlineError>{inlineError}</InlineError>}
          <InputArea>
            <ChatInput
              placeholder={intl.formatMessage({
                id: 'aiAgentChat.input.placeholder',
              })}
              fullWidth={true}
              multiline={true}
              maxRows={4}
              inputProps={{
                'aria-label': intl.formatMessage({
                  id: 'aiAgentChat.input.placeholder',
                }),
              }}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.send' })}>
              <span>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={isStreaming || inputValue.trim() === ''}
                >
                  <SendIcon />
                </SendButton>
              </span>
            </Tooltip>
          </InputArea>
        </ChatPanel>
      )}
      <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.title' })}>
        <ChatFab
          aria-label={intl.formatMessage({ id: 'aiAgentChat.title' })}
          onClick={() => setOpen(prev => !prev)}
        >
          {open ? <CloseIcon /> : <ChatIcon />}
        </ChatFab>
      </Tooltip>
    </>
  )
}
